import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import { SectionHeader, Grad } from '@/components/ui/Section';

const CustomFormulations = () => {
  const capabilities = [
    {
      id: 'cap_surface_treatment',
      title: 'Surface Treatment Options',
      description:
        'Customize graphene surface chemistry for optimal compatibility with your matrix material',
      features: [
        'Hydrophobic/hydrophilic modifications',
        'Functional group attachments',
        'Polymer grafting',
        'Silane coupling agents',
      ],

      icon: 'SparklesIcon',
    },
    {
      id: 'cap_particle_engineering',
      title: 'Particle Engineering',
      description: 'Precise control over graphene morphology and dimensions',
      features: [
        'Custom particle size distribution',
        'Layer number control (single to few-layer)',
        'Aspect ratio optimization',
        'Porosity engineering',
      ],

      icon: 'CubeTransparentIcon',
    },
    {
      id: 'cap_hybrid_systems',
      title: 'Hybrid Additive Systems',
      description: 'Combine graphene with complementary nanomaterials for synergistic effects',
      features: [
        'Graphene + carbon nanotubes',
        'Graphene + metal nanoparticles',
        'Graphene + ceramic nanoparticles',
        'Multi-functional composites',
      ],

      icon: 'Square3Stack3DIcon',
    },
    {
      id: 'cap_application_specific',
      title: 'Application-Specific Grades',
      description: 'Formulations engineered for your exact use case and performance requirements',
      features: [
        'Temperature resistance optimization',
        'Chemical compatibility tuning',
        'Electrical property targeting',
        'Regulatory compliance assurance',
      ],

      icon: 'AdjustmentsHorizontalIcon',
    },
  ];

  return (
    <section className="section relative overflow-hidden bg-ink text-white">
      {/* Shared technical artwork under the standard navy overlay — same
          treatment as the Technical Advantages band for a consistent feel. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/assets/images/Technical-Advantages-bg.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-ink/60 sm:bg-ink/50" />

      <div className="container-page relative">
        {/* Header — existing copy, standardised presentation. */}
        <SectionHeader
          title={
            <>
              Custom <Grad>Formulations</Grad>
            </>
          }
          eyebrow="R&D Services"
          lead="Tailored graphene solutions engineered for your unique material challenges"
        />

        {/* Capability cards — 2 x 2 on desktop, equal heights. */}
        <div
          data-reveal-stagger=""
          className="mt-11 grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-6"
        >
          {capabilities.map((capability) => (
            <div
              key={capability.id}
              className="group relative flex min-w-0 flex-col overflow-hidden rounded-xl border border-white/10 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_18px_45px_rgba(2,8,23,0.4)] transition-[border-color,transform,box-shadow] duration-base ease-out hover:-translate-y-0.5 hover:border-accent/45 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_22px_55px_rgba(2,8,23,0.5),0_0_24px_rgba(14,165,233,0.1)] motion-reduce:hover:transform-none sm:p-7"
              style={{
                background:
                  'linear-gradient(160deg, rgba(13,28,54,0.72) 0%, rgba(10,19,34,0.62) 100%)',
              }}
            >
              {/* Faint technical-dot detail, top-right (per reference). */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute right-5 top-5 h-12 w-16 opacity-[0.14]"
                style={{
                  backgroundImage: 'radial-gradient(rgba(125,211,252,0.9) 1px, transparent 1px)',
                  backgroundSize: '9px 9px',
                }}
              />

              <div className="flex items-start gap-5">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-accent shadow-[0_0_18px_rgba(14,165,233,0.16),inset_0_0_14px_rgba(14,165,233,0.07)] transition-[border-color,box-shadow] duration-base ease-out group-hover:border-accent group-hover:shadow-[0_0_26px_rgba(14,165,233,0.28),inset_0_0_16px_rgba(14,165,233,0.1)] sm:h-16 sm:w-16">
                  <Icon name={capability.icon as any} size={26} />
                </span>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold leading-snug text-white sm:text-xl">
                    {capability.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">
                    {capability.description}
                  </p>
                </div>
              </div>

              {/* Short blue divider under the description row. */}
              <div
                aria-hidden="true"
                className="ml-[4.75rem] mt-5 h-px w-24 bg-gradient-to-r from-accent/60 to-transparent sm:ml-[5.25rem]"
              />

              <ul className="mt-5 flex-1 space-y-3">
                {capability.features.map((feature, index) => (
                  <li
                    key={`feature_${index}`}
                    className="flex items-start gap-3 text-sm text-slate-200"
                  >
                    <Icon
                      name="CheckCircleIcon"
                      size={18}
                      className="mt-0.5 shrink-0 text-accent"
                    />
                    <span className="min-w-0">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* R&D Partnership — existing content, restyled onto the dark ladder. */}
        <div className="mt-14 grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
          <div className="min-w-0">
            <h3 className="t-h2 text-white">R&D Partnership Approach</h3>
            <p className="t-body mt-4 text-slate-300">
              Our materials scientists work directly with your team to develop custom graphene
              formulations that meet your exact specifications and performance targets.
            </p>

            <div className="mt-7 space-y-5">
              {[
                {
                  icon: 'UserGroupIcon',
                  title: 'Dedicated Technical Team',
                  body: 'PhD-level materials scientists assigned to your project',
                },
                {
                  icon: 'BeakerIcon',
                  title: 'State-of-the-Art Laboratory',
                  body: 'Advanced characterization and testing equipment',
                },
                {
                  icon: 'ClockIcon',
                  title: 'Rapid Development Cycle',
                  body: 'Typical formulation development in 2-20 weeks',
                },
                {
                  icon: 'ShieldCheckIcon',
                  title: 'IP Protection',
                  body: 'Confidentiality agreements and custom formulation exclusivity',
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-accent/40 bg-accent/10 text-accent">
                    <Icon name={item.icon as any} size={19} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[0.9375rem] font-semibold text-white">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block text-sm text-slate-400">{item.body}</span>
                  </span>
                </div>
              ))}
            </div>

            <Link
              href="/contact"
              className="group/cta mt-8 inline-flex min-h-[50px] w-fit items-center gap-2.5 rounded-md bg-accent px-7 text-[0.8125rem] font-semibold uppercase tracking-[0.12em] text-white shadow-[0_2px_14px_rgba(14,165,233,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-[background-color,box-shadow,transform] duration-fast ease-out hover:bg-sky-400 hover:shadow-[0_5px_20px_rgba(14,165,233,0.45)] active:translate-y-px motion-reduce:active:transform-none"
            >
              Discuss Your Project
              <Icon
                name="ArrowRightIcon"
                size={18}
                className="transition-transform duration-fast ease-out group-hover/cta:translate-x-[3px] motion-reduce:transform-none"
              />
            </Link>
          </div>

          <div className="relative min-w-0 pb-6 sm:pb-0">
            <div className="overflow-hidden rounded-xl border border-accent/35 shadow-[0_24px_60px_rgba(2,8,23,0.5)]">
              <div className="relative aspect-[3/4]">
                <AppImage
                  src="/assets/images/lab.png"
                  alt="Materials science laboratory with advanced equipment for graphene formulation development and testing"
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="h-full w-full object-cover"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-t from-[#060c18]/55 via-transparent to-transparent"
                />
              </div>
            </div>
            <div className="bg-accent-rich absolute -bottom-4 left-2 max-w-[85%] rounded-lg px-5 py-4 shadow-[0_12px_30px_rgba(2,8,23,0.5),0_0_20px_rgba(14,165,233,0.2)] sm:-bottom-6 sm:-left-6 sm:px-6 sm:py-5">
              <p className="t-value text-3xl font-bold leading-none text-white">50+</p>
              <p className="mt-1.5 text-xs font-medium uppercase tracking-wider text-white/85">
                Custom Formulations
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomFormulations;
